import clsx from 'clsx';
import { type FunctionComponent, useEffect, useId, useMemo, useState } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import { Input } from '~/ui/core/input/Input';
import { RichEditor } from '~/ui/core/rich-editor/RichEditor';

import type { GrowthMeasure } from '../../data-contract/growth';
import { useCreateGoal } from '../api/growth-mutations';
import { useGoalRecommendations, useMyGoals } from '../api/growth-queries';
import { CompanyGoalPicker } from '../components/CompanyGoalPicker';
import { FormField } from '../components/FormField';
import { DetailSection, GrowthTileFrame, GrowthTileSkeleton } from '../components/GrowthTileFrame';
import { PANEL_TITLE_CLASS_NAME } from '../components/panel-title';
import { type RecommendationDraft, useRecommendationDrafts } from '../onboarding/recommendation-drafts-store';
import { recommendationDraftOf } from '../recommendation';
import { type MeasureChoice, MeasurePicker, type NumberFields } from './MeasurePicker';

const toMeasure = (choice: MeasureChoice, numbers: NumberFields): GrowthMeasure | null => {
    switch (choice) {
        case 'none':
            return null;
        case 'done':
            return { isDone: false, type: 'done' };
        case 'percent':
            return { current: 0, type: 'percent' };
        case 'number':
            return {
                current: Number(numbers.start),
                start: Number(numbers.start),
                target: Number(numbers.target),
                type: 'number',
                unit: numbers.unit,
            };
    }
};

const GUIDANCE_LINES = ['what', 'how', 'when', 'why'] as const;

const measureChoiceFor = (measure: GrowthMeasure | null | undefined): MeasureChoice =>
    measure?.type === 'number' || measure?.type === 'percent' || measure?.type === 'done' ? measure.type : 'none';

const NewGoalForm: FunctionComponent<{
    readonly initial?: RecommendationDraft;
    readonly onClose: () => void;
    readonly onCreated: (goalId: string) => void;
    readonly recommendationId?: string;
}> = ({ initial, onClose, onCreated, recommendationId }) => {
    const translate = useTranslate();
    const myGoalsQuery = useMyGoals();
    const createGoal = useCreateGoal();
    const saveDraft = useRecommendationDrafts((state) => state.saveDraft);
    const settle = useRecommendationDrafts((state) => state.settle);
    const titleId = useId();
    const initialMeasure = initial?.measure;
    const [title, setTitle] = useState(initial?.title ?? '');
    const [description, setDescription] = useState(initial?.description ?? '');
    const [measureChoice, setMeasureChoice] = useState<MeasureChoice>(measureChoiceFor(initialMeasure));
    // The picker has no milestone option; keep a suggested milestone measure until the person picks another one.
    const [keptMeasure, setKeptMeasure] = useState(initialMeasure?.type === 'milestones' ? initialMeasure : null);
    const [numbers, setNumbers] = useState<NumberFields>(
        initialMeasure?.type === 'number'
            ? { start: String(initialMeasure.start), target: String(initialMeasure.target), unit: initialMeasure.unit }
            : { start: '', target: '', unit: '' },
    );
    const [due, setDue] = useState(initial?.due ?? '');
    const [paysIntoId, setPaysIntoId] = useState<string | null>(initial?.paysIntoId ?? null);
    const companyGoals = myGoalsQuery.data?.companyGoals ?? [];
    const draft: RecommendationDraft = useMemo(
        () => ({
            description,
            due: due.length === 0 ? null : due,
            measure: keptMeasure ?? toMeasure(measureChoice, numbers),
            paysIntoId,
            suggestionId: recommendationId ?? null,
            title: title.trim(),
        }),
        [description, due, keptMeasure, measureChoice, numbers, paysIntoId, recommendationId, title],
    );

    useEffect(() => {
        if (recommendationId !== undefined) {
            saveDraft(recommendationId, draft);
        }
    }, [draft, recommendationId, saveDraft]);

    const submit = (share: boolean): void =>
        createGoal.mutate(
            { ...draft, share },
            {
                onSuccess: (goal) => {
                    if (recommendationId !== undefined) {
                        settle(recommendationId, share ? 'sent' : 'kept-private');
                    }
                    onCreated(goal.id);
                },
            },
        );

    const isFiniteNumber = (value: string): boolean => value.trim().length > 0 && Number.isFinite(Number(value));
    const isMeasureComplete =
        measureChoice !== 'number' || (isFiniteNumber(numbers.start) && isFiniteNumber(numbers.target));
    const canSubmit = title.trim().length > 0 && isMeasureComplete && !createGoal.isPending;

    return (
        <GrowthTileFrame
            actions={
                <>
                    <Button disabled={!canSubmit} size="sm" variant="secondary" onClick={() => submit(false)}>
                        {translate('pages.growth.actions.keep-private')}
                    </Button>
                    <Button disabled={!canSubmit} size="sm" variant="primary" onClick={() => submit(true)}>
                        {translate('pages.growth.actions.share-with-manager')}
                    </Button>
                </>
            }
            onClose={onClose}
        >
            <header className="flex w-full flex-col gap-group">
                <label className="sr-only" htmlFor={titleId}>
                    {translate('pages.growth.form.title-label')}
                </label>
                <Input
                    // biome-ignore lint/a11y/noAutofocus: the panel opens for this one field
                    autoFocus={true}
                    className={clsx(PANEL_TITLE_CLASS_NAME, 'w-full placeholder:text-text-placeholder')}
                    variant="ghost"
                    id={titleId}
                    placeholder={translate('pages.growth.form.title-placeholder')}
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                />
            </header>

            <div className="flex w-full flex-col gap-m">
                <DetailSection title={translate('pages.growth.form.guidance-title')}>
                    <ul className="m-0 flex list-disc flex-col gap-xs pl-xxl text-body-s text-text-secondary">
                        {GUIDANCE_LINES.map((line) => (
                            <li key={line}>{translate(`pages.growth.form.guidance.${line}`)}</li>
                        ))}
                    </ul>
                    <span className="text-body-xs text-text-muted">
                        {translate('pages.growth.form.guidance-footnote')}
                    </span>
                </DetailSection>
            </div>
            <div className="flex w-full flex-col gap-s">
                <span className="text-body-s font-medium text-text-heading">
                    {translate('pages.growth.form.description-label')}
                </span>
                <div className="overflow-hidden rounded-sm border border-border-control">
                    <RichEditor
                        changeDebounceMs={0}
                        ariaLabel={translate('pages.growth.form.description-label')}
                        placeholder={translate('pages.growth.form.description-placeholder')}
                        value={description}
                        onChange={setDescription}
                    />
                </div>
            </div>

            <div className="flex w-full flex-col gap-m">
                <span className="text-body-s font-medium text-text-heading">
                    {translate('pages.growth.form.measure-label')}
                </span>
                <MeasurePicker
                    choice={measureChoice}
                    numbers={numbers}
                    onChoiceChange={(choice) => {
                        setKeptMeasure(null);
                        setMeasureChoice(choice);
                    }}
                    onNumbersChange={setNumbers}
                />
            </div>

            <FormField label={translate('pages.growth.form.due-label')}>
                {(id) => <Input id={id} type="date" value={due} onChange={(event) => setDue(event.target.value)} />}
            </FormField>

            {companyGoals.length === 0 ? null : (
                <div className="flex w-full flex-col gap-s">
                    <span className="text-body-s font-medium text-text-heading">
                        {translate('pages.growth.form.pays-into-label')}
                    </span>
                    <CompanyGoalPicker companyGoals={companyGoals} value={paysIntoId} onChange={setPaysIntoId} />
                </div>
            )}
        </GrowthTileFrame>
    );
};

export const NewGoalTile: FunctionComponent<{
    readonly onClose: () => void;
    readonly onCreated: (goalId: string) => void;
    readonly recommendationId?: string;
}> = ({ onClose, onCreated, recommendationId }) => {
    const translate = useTranslate();
    const recommendationsQuery = useGoalRecommendations(recommendationId !== undefined);
    const savedDraft = useRecommendationDrafts((state) =>
        recommendationId === undefined ? undefined : state.drafts[recommendationId],
    );
    const [initialDraft] = useState(savedDraft);

    if (recommendationId !== undefined && initialDraft === undefined && recommendationsQuery.isPending) {
        return <GrowthTileSkeleton onClose={onClose} />;
    }

    const recommendation = recommendationsQuery.data?.recommendations.find(({ id }) => id === recommendationId);
    const initial =
        initialDraft ??
        (recommendation === undefined
            ? undefined
            : recommendationDraftOf(recommendation, translate('pages.growth.onboarding.tracking-label')));

    return (
        <NewGoalForm initial={initial} recommendationId={recommendationId} onClose={onClose} onCreated={onCreated} />
    );
};
